// src/pages/Login/NiceLogin.jsx
import React from "react";
import {
  Box,
  Stack,
  Typography,
  Avatar,
  CircularProgress,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { login, userInfo } from "../../services/accountsService";
import BackgroundSlideshow from "../../shared/BackgroundSlideshow";
import GlassCard from "../../shared/GlassCard";
import NiceLoginForm from "./NiceLoginForm";

const asset = (path) => {
  const base = process.env.PUBLIC_URL || "";
  return `${base}/${path.replace(/^\/+/, "")}`;
};
const BACKGROUNDS = [
  asset("/assets/bg1.jpg"),
  asset("/assets/bg2.jpg"),
  asset("/assets/bg3.jpg"),
  asset("/assets/bg4.jpg"),
  asset("/assets/bg5.jpg"),
];

const LOGO_URL = asset("/assets/cenzor_logo.png");

const NiceLogin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = React.useState(false);

  const handleLogin = async (event) => {
    event.preventDefault();
    if (loading) return;

    const data = new FormData(event.currentTarget);
    const username = data.get("username");
    const password = data.get("password");

    setLoading(true);
    try {
      const response = await login(username, password);
      const user = (await userInfo()).data;

      if (response.success) {
        if (user.role === "admin") {
          navigate("/tenant-settings?menu=1");
        } else if (user.role === "anonymizer") {
          navigate("/pii-anonymizer?menu=1");
        } else if (user.is_superuser) {
          navigate("/admin/billing?menu=1");
        }
        else {
          navigate("/about-app?menu=1");
        }
      } else {
        enqueueSnackbar(
          t("login_page.login_failed", { error: response.error }),
          { variant: "error" }
        );
      }
    } catch (error) {
      enqueueSnackbar(
        t("login_page.login_failed", { error: error.message }),
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        position: "relative",
        overflow: "hidden",
        isolation: "isolate",
        px: 2,
      }}
    >
      <BackgroundSlideshow images={BACKGROUNDS} />

      <Stack
        spacing={{ xs: 4.5, sm: 5.5 }}
        alignItems="center"
        sx={{ width: "min(92vw, 520px)" }}
      >
        {/* Logo on top (optional) */}
        <Box
          component="img"
          src={LOGO_URL}
          alt=""
          sx={{
            height: { xs: 120, sm: 140 },
            width: "auto",
            userSelect: "none",
            pointerEvents: "none",
            filter: "drop-shadow(0 8px 18px rgba(0,0,0,.45))",
          }}
        />

        <GlassCard>
          <Stack spacing={3} alignItems="center" sx={{ py: 1 }}>
            <Avatar
              sx={{
                bgcolor: "rgba(255,255,255,0.16)",
                border: "1px solid rgba(255,255,255,.55)",
                boxShadow: "0 10px 26px rgba(0,0,0,.25)",
                backdropFilter: "blur(8px)",
              }}
            >
              <LockOutlinedIcon />
            </Avatar>

            <Stack spacing={0.5} alignItems="center">
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  letterSpacing: 0.3,
                  textTransform: "uppercase",
                  opacity: 0.9,
                }}
              >
                {t("login_page.sign_in")}
              </Typography>
              <Typography
                variant="caption"
                sx={{ opacity: 0.7, fontStyle: "italic" }}
              >
                secure access portal
              </Typography>
            </Stack>

            <NiceLoginForm
              handleLogin={handleLogin}
              navigate={navigate}
              loading={loading}
            />

            {loading && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mt: 1 }}
              >
                <CircularProgress size={18} />
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {t("login_form.signing_in", "Signing you in…")}
                </Typography>
              </Stack>
            )}
          </Stack>
        </GlassCard>
      </Stack>
    </Box>
  );
};

export default NiceLogin;
